// src/components/PeoplePlan/EmployeeManagement/hooks/useEmployeeData.js
import { useState, useEffect, useCallback } from "react";
import apiClient from "../../../../apiClient";

export const useEmployeeData = (selectedMUM, roleConfig) => {
  const [employees, setEmployees] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Helper function to compare tenure
  const compareTenure = (a, b) => {
    const aTotalMonths = (a.tenure?.years || 0) * 12 + (a.tenure?.months || 0);
    const bTotalMonths = (b.tenure?.years || 0) * 12 + (b.tenure?.months || 0);
    return bTotalMonths - aTotalMonths; // Descending order (highest tenure first)
  };

  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.get(
        `${roleConfig.apiEndpoint}?mum=${encodeURIComponent(
          selectedMUM
        )}&ts=${Date.now()}`
      );

      const data = response.data;

      const employeesByStore = data.map((store) => {
        const uniqueEmployeesMap = new Map();

        store.employees.forEach((employee) => {
          if (
            !uniqueEmployeesMap.has(employee.user_id) ||
            (employee.workflow_name &&
              !uniqueEmployeesMap.get(employee.user_id).workflow_name)
          ) {
            uniqueEmployeesMap.set(employee.user_id, {
              ...employee,
              tenure: employee.tenure || { years: 0, months: 0 },
              workflow: employee.workflow_name || null,
              corp: store.corp,
            });
          }
        });

        // Get employees array and sort by tenure (descending) by default
        let storeEmployees = Array.from(uniqueEmployeesMap.values());

        // Apply default tenure sorting for sortable roles
        if (roleConfig.sortable) {
          storeEmployees.sort(compareTenure);
        }

        return {
          storeName: store.corp,
          employees: storeEmployees,
          positionRequests: store.positionRequests || [], // ADD THIS LINE to preserve position requests
        };
      });

      setEmployees(employeesByStore);
      const allUserIds = employeesByStore.flatMap((store) =>
        store.employees.map((emp) => emp.user_id)
      );

      if (allUserIds.length > 0) {
        try {
          const statusResponse = await apiClient.post(
            "/api/employee-status/batch",
            { user_ids: allUserIds }
          );

          const statusMap = statusResponse.data;

          // Add status to each employee
          employeesByStore.forEach((store) => {
            store.employees.forEach((emp) => {
              emp.status_indicator = statusMap[emp.user_id] || null;
            });
          });
        } catch (error) {
          console.error("Error fetching status indicators:", error);
        }
      }
      setTotalCount(
        employeesByStore.reduce(
          (total, store) => total + store.employees.length,
          0
        )
      );
    } catch (error) {
      console.error(`Error fetching ${roleConfig.displayName}:`, error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [selectedMUM, roleConfig]);

  useEffect(() => {
    if (selectedMUM && roleConfig) {
      fetchEmployees();
    }
  }, [selectedMUM, roleConfig, fetchEmployees]);

  return { employees, totalCount, loading, error, refetch: fetchEmployees };
};
